import { Prompt } from '@/components/Prompt';
import { useAuth } from '@/services/auth';
import { SupportedLanguage } from '@/utils/types';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CloneVoiceLanguageSelect } from './CloneVoiceLanguageSelect';
import { isRecordingCloneVoice } from './cloneVoiceModalState';
import { NameYourVoice, SubTitle, TitleWithBackButton } from './common';
import RecordingControls, { Props as RecordingControlsProps } from './RecordingControls';
import { consentContentByLanguage, displayLanguages, recordTextByLanguage } from './textByLanguage';

const StyledSubTitle = styled(SubTitle)`
    margin-top: 16px;
    margin-bottom: 32px;
`;

const StyledPrompt = styled(Prompt)<{ isRecording: boolean }>`
    margin-bottom: 32px;
    ${({ isRecording, theme }) => isRecording && `background-color: var(--Danger-Light, ${theme.colors.primary.bgLight});`}
`;

interface Props extends Pick<RecordingControlsProps, 'onSubmit'> {
    onBackClick: () => void;
}

function RecordVoiceDialogContent({ onBackClick, onSubmit }: Props) {
    const [language, setLanguage] = useState<SupportedLanguage>('english');
    const [voiceName, setVoiceName] = useState('');
    const isRecording = useRecoilValue(isRecordingCloneVoice);
    const { user } = useAuth();

    const defaultName = user?.given_name || 'Untitled voice';

    return (
        <>
            <TitleWithBackButton title="Record your voice" onBackClick={onBackClick} />
            <StyledSubTitle variant="body1">Please record your voice reading the following script & consent form out loud.</StyledSubTitle>
            <CloneVoiceLanguageSelect
                value={language}
                onChange={nextLanguage => {
                    setLanguage(nextLanguage);
                }}
            />
            <StyledPrompt
                isRecording={isRecording}
                language={language}
                languages={displayLanguages}
                primaryTextByLanguage={consentContentByLanguage}
                secondaryTextByLanguage={recordTextByLanguage}
            />
            <NameYourVoice
                value={voiceName}
                onChange={e => {
                    setVoiceName(e.target.value);
                }}
                placeholder={defaultName}
            />
            <RecordingControls onSubmit={onSubmit} language={language} voiceName={voiceName} />
        </>
    );
}

export default RecordVoiceDialogContent;
